/**
 * Disconnected Accounts Hook
 *
 * Single source of truth for the disconnection banner (see
 * components/layout/DisconnectedAccountBanner.tsx). Returns the ordered list of
 * integrations that have been unexpectedly disconnected and should prompt the
 * user to reconnect.
 *
 * Dual-mode (Pattern 00):
 *  - Preview: driven by the `preview-disconnected-account` localStorage key,
 *    set by the debug buttons in PersonDetailSheet. No backend calls.
 *  - Production: derived from the real `needsReconnect` state of useAccounts
 *    (personal: Google / Microsoft / Slack) and useIntegrations (workspace:
 *    HubSpot / Salesforce). Workspace integrations are gated to admins/owners.
 *
 * Both the personal/workspace classification (`VARIANT_SCOPE`) and the preview
 * setter (`setDisconnectedAccountPreview`) live here rather than in the banner
 * so the banner and PersonDetailSheet can import them without a cycle.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { isLovablePreview } from '@/lib/utils/environment';
import { useAccounts } from './use-accounts';
import { useIntegrations } from './use-integrations';
import { useCurrentUser } from './use-current-user';
import { IntegrationPlatform, UserIntegrationPlatform } from '@/lib/graphql/types-and-hooks';

export type DisconnectedAccountVariant =
  | 'google'
  | 'microsoft'
  | 'crm-hubspot'
  | 'crm-salesforce'
  | 'slack';

/**
 * personal = destructive/red framing, shown to the affected user only,
 *            non-dismissible (email/calendar sync is broken until reconnect).
 * workspace = amber/warning framing, dismissible (core product still works).
 */
export const VARIANT_SCOPE: Record<DisconnectedAccountVariant, 'personal' | 'workspace'> = {
  google: 'personal',
  microsoft: 'personal',
  'crm-hubspot': 'workspace',
  'crm-salesforce': 'workspace',
  slack: 'workspace',
};

// Display order: urgent personal accounts before workspace integrations.
const VARIANT_ORDER: DisconnectedAccountVariant[] = [
  'google',
  'microsoft',
  'crm-hubspot',
  'crm-salesforce',
  'slack',
];

const ALL_VARIANTS = new Set<string>(VARIANT_ORDER);

const STORAGE_KEY = 'preview-disconnected-account';
const CHANGE_EVENT = 'disconnected-account-changed';
// Production dismissals are per-session only (sessionStorage) so a dismissed
// workspace banner reappears next session if the integration is still down,
// and dismissal never mutates backend state.
const DISMISS_KEY = 'disconnected-account-dismissed';

// Roles allowed to see workspace-integration banners.
const ADMIN_ROLES = new Set(['owner', 'admin']);

function readPreviewVariants(): DisconnectedAccountVariant[] {
  if (typeof window === 'undefined') return [];
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  return raw
    .split(',')
    .map(s => s.trim())
    .filter((s): s is DisconnectedAccountVariant => ALL_VARIANTS.has(s));
}

/**
 * Set the preview disconnection state. Accepts a single variant, a list (to
 * preview the consolidated multi-banner), or null to clear. Used by the
 * PersonDetailSheet debug panel.
 */
export function setDisconnectedAccountPreview(
  variant: DisconnectedAccountVariant | DisconnectedAccountVariant[] | null,
) {
  const list = variant == null ? [] : Array.isArray(variant) ? variant : [variant];
  if (list.length > 0) {
    localStorage.setItem(STORAGE_KEY, list.join(','));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function readDismissed(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  const raw = sessionStorage.getItem(DISMISS_KEY);
  return new Set(raw ? raw.split(',').filter(Boolean) : []);
}

export interface UseDisconnectedAccountsResult {
  /** Ordered, not-dismissed variants that should currently show a banner. */
  variants: DisconnectedAccountVariant[];
  /** Dismiss a single banner (workspace scope only — personal are persistent). */
  dismiss: (variant: DisconnectedAccountVariant) => void;
}

export function useDisconnectedAccounts(): UseDisconnectedAccountsResult {
  const preview = isLovablePreview();
  const { accounts } = useAccounts();
  const { allIntegrations } = useIntegrations();
  const { currentUser } = useCurrentUser();
  const isAdmin = ADMIN_ROLES.has(currentUser?.role ?? '');

  // --- Preview state (localStorage), reactive via custom + storage events ---
  const [previewVariants, setPreviewVariants] = useState<DisconnectedAccountVariant[]>(
    () => readPreviewVariants(),
  );
  useEffect(() => {
    if (!preview) return;
    const handler = () => setPreviewVariants(readPreviewVariants());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, [preview]);

  // --- Production state (session dismissals) ---
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());

  // Production: map real disconnections to banner variants.
  const productionVariants = useMemo<DisconnectedAccountVariant[]>(() => {
    if (preview) return [];
    const out: DisconnectedAccountVariant[] = [];

    // Personal accounts (per-user). Shown to the affected user regardless of role.
    for (const acct of accounts) {
      if (!acct.needsReconnect) continue;
      if (acct.platform === UserIntegrationPlatform.GoogleCalendar) out.push('google');
      else if (acct.platform === UserIntegrationPlatform.Microsoft) out.push('microsoft');
      else if (acct.platform === UserIntegrationPlatform.Slack) out.push('slack');
      // Zoom has no banner variant — intentionally skipped.
    }

    // Workspace integrations — admins/owners only.
    if (isAdmin) {
      for (const integ of allIntegrations) {
        if (!integ.needsReconnect) continue;
        if (integ.platform === IntegrationPlatform.HubspotMain) out.push('crm-hubspot');
        else if (integ.platform === IntegrationPlatform.SalesforceMain) out.push('crm-salesforce');
      }
    }

    return out;
  }, [preview, accounts, allIntegrations, isAdmin]);

  const variants = useMemo<DisconnectedAccountVariant[]>(() => {
    const candidates = preview ? previewVariants : productionVariants;
    const present = new Set(candidates);
    return VARIANT_ORDER.filter(v => present.has(v) && !dismissed.has(v));
  }, [preview, previewVariants, productionVariants, dismissed]);

  const dismiss = useCallback(
    (variant: DisconnectedAccountVariant) => {
      if (preview) {
        // Preview: drop just this variant from the stored list.
        setDisconnectedAccountPreview(readPreviewVariants().filter(v => v !== variant));
        return;
      }
      // Production: remember the dismissal for this session only.
      setDismissed(prev => {
        const next = new Set(prev).add(variant);
        sessionStorage.setItem(DISMISS_KEY, Array.from(next).join(','));
        return next;
      });
    },
    [preview],
  );

  return { variants, dismiss };
}
